// Ticket 1652: het trainingsresultaat met "Gewicht volgende keer" per oefening.
//
// - Ingevulde gewichten gaan als concept in localStorage (per rapport), zodat
//   een refresh of een per ongeluk gesloten tabblad niets kost.
// - Opslaan gaat in één verzoek (fetch, JSON). Tijdens het opslaan is de knop
//   uitgeschakeld; een tweede klik doet niets. De server is daarnaast
//   idempotent, dus ook een dubbel verzoek geeft geen dubbele registratie.
// - Mislukt het opslaan, dan blijft alles staan en verschijnt een melding.
// - Zonder JavaScript werkt het formulier gewoon met een redirect.
import { Controller } from "stimulus"

const CONCEPT_PREFIX = "cc-trainingsresultaat-concept-"
const CONCEPT_MAX_LEEFTIJD = 7 * 24 * 60 * 60 * 1000

export default class extends Controller {
  static targets = ["formulier", "gewicht", "hint", "knop", "knopTekst", "fout", "concept", "voltooid", "opgeslagen"]
  static values = { conceptSleutel: String, teksten: Object }

  connect() {
    this.bezig = false
    this.herstelConcept()
    this.gewichtTargets.forEach((input) => this.werkHintBij(input))
    this.toetsHandler = (event) => { if (event.key === "Escape") this.sluitOpen() }
    document.addEventListener("keydown", this.toetsHandler)
    const open = this.openOverlay()
    if (open) this.focusIn(open)
    this.ruimAdresOp()
  }

  // De pop-ups horen bij dit ene moment. Na een refresh niet opnieuw tonen.
  ruimAdresOp() {
    try {
      const url = new URL(window.location.href)
      const eenmalig = ["voltooid", "intensiteit_opgeslagen", "opslaan_mislukt"]
      if (!eenmalig.some((naam) => url.searchParams.has(naam))) return
      eenmalig.forEach((naam) => url.searchParams.delete(naam))
      window.history.replaceState(window.history.state, "", url.toString())
    } catch (e) {
      // oude browser: dan blijft het adres zoals het was
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.toetsHandler)
  }

  gewijzigd(event) {
    const input = event.target
    input.removeAttribute("aria-invalid")
    this.werkHintBij(input)
    this.bewaarConcept()
  }

  async opslaan(event) {
    event.preventDefault()
    if (this.bezig) return

    if (!this.controleer()) {
      this.toonFout(this.tekstenValue.ongeldig)
      return
    }

    this.bezig = true
    this.verbergFout()
    const origineel = this.hasKnopTekstTarget ? this.knopTekstTarget.textContent : ""
    this.knopTarget.disabled = true
    this.knopTarget.setAttribute("aria-busy", "true")
    if (this.hasKnopTekstTarget) this.knopTekstTarget.textContent = this.tekstenValue.bezig

    try {
      const token = document.querySelector("meta[name='csrf-token']")?.content
      const antwoord = await fetch(this.formulierTarget.action, {
        method: "POST",
        body: new FormData(this.formulierTarget),
        headers: { "Accept": "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
        credentials: "same-origin"
      })
      let data = {}
      try { data = await antwoord.json() } catch (e) { data = {} }

      if (antwoord.ok && data.ok) {
        this.wisConcept()
        this.toonOverlay(this.opgeslagenTarget)
      } else {
        this.markeerOngeldig(data.ongeldig || [])
        this.toonFout(data.fout || this.tekstenValue.opslaan)
      }
    } catch (e) {
      this.toonFout(this.tekstenValue.netwerk)
    } finally {
      this.bezig = false
      this.knopTarget.disabled = false
      this.knopTarget.removeAttribute("aria-busy")
      if (this.hasKnopTekstTarget) this.knopTekstTarget.textContent = origineel
    }
  }

  bekijkResultaat(event) {
    this.sluit(event)
    const doel = document.getElementById("oefeningen")
    if (doel) doel.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  sluit(event) {
    const overlay = event.currentTarget.closest(".tr-overlay")
    if (overlay) overlay.hidden = true
  }

  sluitOpen() {
    const open = this.openOverlay()
    if (open) open.hidden = true
  }

  // --- hulpfuncties ---

  openOverlay() {
    return this.element.querySelector(".tr-overlay:not([hidden])")
  }

  toonOverlay(overlay) {
    overlay.hidden = false
    this.focusIn(overlay)
  }

  focusIn(overlay) {
    const knop = overlay.querySelector("[data-autofocus]")
    if (knop) knop.focus()
  }

  getal(waarde) {
    const tekst = String(waarde || "").trim().replace(",", ".")
    if (!/^\d{1,4}(\.\d{1,3})?$/.test(tekst)) return NaN
    return parseFloat(tekst)
  }

  opmaak(getal) {
    return getal.toLocaleString("nl-NL", { maximumFractionDigits: 1 })
  }

  controleer() {
    let goed = true
    this.gewichtTargets.forEach((input) => {
      const leeg = input.value.trim() === ""
      if (!leeg && isNaN(this.getal(input.value))) {
        input.setAttribute("aria-invalid", "true")
        goed = false
      } else {
        input.removeAttribute("aria-invalid")
      }
    })
    return goed
  }

  markeerOngeldig(ids) {
    ids.forEach((id) => {
      const input = this.gewichtTargets.find((el) => el.dataset.contentId === String(id))
      if (input) input.setAttribute("aria-invalid", "true")
    })
  }

  werkHintBij(input) {
    const hint = this.hintTargets.find((el) => el.dataset.contentId === input.dataset.contentId)
    if (!hint) return

    const basis = parseFloat(input.dataset.basis)
    const waarde = this.getal(input.value)
    if (isNaN(waarde) || isNaN(basis) || basis <= 0) {
      hint.textContent = input.dataset.bronTekst || ""
      return
    }

    const verschil = Math.round((waarde - basis) * 100) / 100
    if (verschil === 0) {
      hint.textContent = this.tekstenValue.gelijk
      return
    }
    const sjabloon = verschil > 0 ? this.tekstenValue.hoger : this.tekstenValue.lager
    hint.textContent = sjabloon
      .replace("%{kg}", this.opmaak(Math.abs(verschil)))
      .replace("%{pct}", Math.round((Math.abs(verschil) / basis) * 100))
  }

  toonFout(tekst) {
    if (!this.hasFoutTarget) return
    this.foutTarget.textContent = tekst
    this.foutTarget.hidden = false
  }

  verbergFout() {
    if (this.hasFoutTarget) this.foutTarget.hidden = true
  }

  conceptSleutel() {
    return CONCEPT_PREFIX + this.conceptSleutelValue
  }

  bewaarConcept() {
    const waarden = {}
    this.gewichtTargets.forEach((input) => { waarden[input.dataset.contentId] = input.value })
    try {
      window.localStorage.setItem(this.conceptSleutel(), JSON.stringify({ waarden, tijd: Date.now() }))
    } catch (e) {
      // Geen opslag beschikbaar (privévenster, volle opslag): het formulier werkt gewoon door.
    }
  }

  herstelConcept() {
    let concept = null
    try {
      concept = JSON.parse(window.localStorage.getItem(this.conceptSleutel()) || "null")
    } catch (e) {
      concept = null
    }
    if (!concept || !concept.waarden) return
    if (!concept.tijd || Date.now() - concept.tijd > CONCEPT_MAX_LEEFTIJD) {
      this.wisConcept()
      return
    }

    let hersteld = false
    this.gewichtTargets.forEach((input) => {
      const waarde = concept.waarden[input.dataset.contentId]
      if (typeof waarde === "string" && waarde !== input.value) {
        input.value = waarde
        hersteld = true
      }
    })
    if (hersteld && this.hasConceptTarget) this.conceptTarget.hidden = false
  }

  wisConcept() {
    try {
      window.localStorage.removeItem(this.conceptSleutel())
    } catch (e) {
      // niets te wissen
    }
    if (this.hasConceptTarget) this.conceptTarget.hidden = true
  }
}
